/* THE CHAMPION, AS THE HEADLINE IT SHOULD ALWAYS HAVE BEEN — pure, no React.
 *
 * Brian, with a screenshot of the SHSAA Golf drawer: *"When I open a
 * championship this is the tiny location of the summary. Fix this."* He circled
 * a one-line band wedged between the header chips and a full-width gold
 * "Create Art" button.
 *
 * ⚠️ THE DEFECT IS THE HIERARCHY, AND IT IS MEASURABLE RATHER THAN A MATTER OF
 * TASTE. The champion — the single most important fact on a championship record,
 * and the thing Brian has standing instructions about (*"you should see the
 * actual championships crowned"*) — rendered as the LAST of five small stat
 * cards plus one line of prose. The Action Console, six buttons, owned the fold.
 * A rep was being asked to act on a fact they had to squint at.
 *
 * ⚠️ AND THE DRAWER RENDERED NONE OF `championIssues()`. The board rows have
 * carried the full flag set the whole time — no source, shared citation, cannot
 * seed a ring, no roster, no title wording. The drawer, which is the surface a
 * person opens specifically to DECIDE whether a crown is right, showed a stat
 * card and a sentence. That is the same shape as the sourceless-crown bug this
 * file's neighbours already fixed once: *"the row's flag was firing correctly
 * the whole time; the place a person opens to decide said nothing."* Fixing the
 * layout without surfacing the flags would have made the wrong fact bigger.
 */

import { championOfRecord, athletesOf, accountSchool } from '../../../lib/championDisplay.js'

/**
 * The champion, EXACTLY as the record holds it.
 *
 * ⚠️ NO TRUNCATION, NO NORMALISATION, NO TIDYING — Brian: *"I told you not to
 * shorten anything."* Whitespace is trimmed because leading spaces are storage
 * noise rather than part of a name; nothing else is touched.
 *
 * 🔴 2026-10-19 — THE CHAMPION OF RECORD IS THE SCHOOL. Brian: "Individual
 * championships are NOT split. The champion of record is the SCHOOL, and the
 * individual athlete and event are attached as the reference."
 *
 * `winningSchool` used to be described here as "the documented fallback the
 * rest of the board already uses". Under this ruling there is nothing to fall
 * back FROM: `current_champion` holds a school on every kind of title, so this
 * returns one line, the school, on both — exactly what it already returned for
 * a team title. The athletes are a separate list, rendered beneath it by the
 * row, because they are a different fact with a different length.
 *
 * ⚠️ STILL NO TRUNCATION AND NO TIDYING, and now there is nothing to compose
 * either. Brian: "I told you not to shorten anything." */
export function fullChampion(c) {
  return championOfRecord(c).trim()
}

/** The athletes to render under the school. `{ ok, lines, error }`. */
export function championAthletes(c) {
  return athletesOf(c)
}

/** The school alone — for anything that looks up an ACCOUNT rather than renders. */
export function championAccount(c) {
  return accountSchool(c)
}

/**
 * THE YEAR, AND WHERE IT CAME FROM.
 *
 * ⚠️ A CROWN WITHOUT A YEAR IS UNCHECKABLE — two different seasons of the same
 * championship have the same name, so "SHSAA Golf Mixed Open · Saskatoon" names
 * no event at all. The year is therefore part of the headline, not a detail.
 *
 * ⚠️ AND IT MUST SAY WHICH YEAR IT IS. `basis` is returned alongside, because a
 * year taken from a PROJECTED date is a guess and must not read like a fact —
 * the same rule DateStamp and StatePanel's DateCell already enforce on dates.
 * The order is strongest-evidence-first: when the crown was actually recorded,
 * then the season the record declares, then the effective date.
 *
 * Returns `{ year, basis, trusted }`, or null when the record cannot answer —
 * and a record that cannot answer says so rather than borrowing today's year.
 */
export function championYear(c) {
  const yr = (v) => {
    const d = v instanceof Date ? v : v ? new Date(v) : null
    const n = d && !Number.isNaN(d.getTime()) ? d.getFullYear() : null
    /* ⚠️ A HubSpot epoch-null parses to 1970, and this object is known to carry
     * them — CLAUDE.md records the count, undated, and I did not re-measure it,
     * so no figure is quoted here. The guard does not need one: a championship
     * year outside a sane window is not a year, it is a storage artefact,
     * whether there is one such record or a thousand. */
    return n && n > 1900 && n < 2200 ? n : null
  }

  const crowned = yr(c?.crownedAt)
  if (crowned) return { year: crowned, basis: 'the date the crown was recorded', trusted: true }

  const season = Number(c?.seasonYear)
  if (season > 1900 && season < 2200) {
    return { year: season, basis: 'the season on the record', trusted: true }
  }

  const eff = yr(c?.effDate) || yr(c?.heldDate) || yr(c?.dateObj)
  if (eff) {
    /* ⚠️ `dateConfidence` IS THE ONLY THING THAT MAKES THIS HONEST. Most of this
     * object's dates are projected, so defaulting to trusted would put a
     * confident year on a guess — which is worse than no year, because nobody
     * would then think to check it. */
    const confirmed = String(c?.dateConfidence || '').toLowerCase() === 'confirmed'
    return {
      year: eff,
      basis: confirmed ? 'the confirmed event date' : 'a projected date, not a sourced one',
      trusted: confirmed,
    }
  }
  return null
}

/**
 * WHAT THE RECORD SAYS ABOUT ITSELF, AND WHERE IT CONTRADICTS ITSELF.
 *
 * ⚠️ REPORTED, NEVER RESOLVED. Brian asked whether the drawer showing `Champion`
 * and `To Pursue` at the same time was correct state or two fields disagreeing.
 * It can be either, and the UI is not entitled to decide: picking one would hide
 * a defect at the source, which is the rule StatePanel already states out loud
 * for the flag-versus-name case (*"Nothing here has been corrected — this needs
 * fixing at the source."*). This is the per-record form of the same report.
 *
 * ⚠️ THE TWO AXES ARE NOT A CONTRADICTION BY THEMSELVES, and that is why this
 * function is narrow. CLAUDE.md is explicit that `sr_pursuit_status` and
 * `sr_champion_status` are deliberately two axes: one drives the map fill, the
 * other drives Action Needed. An early draft of this flagged any mismatch and
 * would have alarmed on thousands of perfectly ordinary records. It fires only
 * where the record asserts two things that cannot both be true of one event.
 */
export function statusDisagreements(c) {
  const out = []
  const name = fullChampion(c)
  const champStatus = String(c?.championStatus || '').trim()
  const pursuit = String(c?.pursuit || '').trim()
  const crownedFlag = Boolean(c?.crowned) || /champion\s*crowned/i.test(champStatus)

  /* 1. The flag says crowned and there is no name, or vice versa. This is the
   *    pair StatePanel counts across a whole state; here it is named on the one
   *    record a person is looking at. */
  if (crownedFlag && !name) {
    out.push({
      key: 'crowned-no-name',
      label: 'Crowned, with no champion recorded',
      why: `The status says ${champStatus || 'champion crowned'}, but no champion name is on the record. `
        + 'One of the two is wrong. Nothing here has been corrected — this needs fixing at the source.',
    })
  }
  if (!crownedFlag && name && champStatus) {
    out.push({
      key: 'name-not-crowned',
      label: 'A champion is recorded but the status does not say crowned',
      why: `The record names ${name} as champion while its champion status reads ${champStatus}. `
        + 'The board treats a recorded winner as a champion either way, so this is a flag that '
        + 'did not get flipped rather than a missing winner. Reported, not corrected.',
    })
  }

  /* 2. ⚠️ BRIAN'S CASE: crowned on one axis, still being chased on the other.
   *    `sr_pursuit_status` has 'Champion Crowned' as its own terminal value, so
   *    a crowned record sitting at 'To Pursue' is not two axes describing
   *    different things — it is the pursuit axis never having been advanced.
   *    Worth saying because a crowned record in the To Pursue column is work
   *    that looks outstanding and is not. */
  if (crownedFlag && name && /^(not started|to pursue)$/i.test(pursuit)) {
    out.push({
      key: 'crowned-still-to-pursue',
      label: `Crowned, but the pursuit status still reads ${pursuit}`,
      why: `${name} is recorded as champion, while the pursuit status is still ${pursuit}. `
        + 'Champion outcome and pursuit work are separate fields. Winner research and sales follow-up '
        + 'must be reviewed separately; a recorded champion does not establish that outreach is complete. '
        + 'The shared next-work display describes the recorded evidence. The stored pursuit status is unchanged.',
    })
  }
  return out
}

/**
 * The result detail that makes a crown credible, in the order a person would
 * say it. Each entry carries its own label so the renderer states what it is
 * showing rather than printing four bare fragments a reader has to assemble —
 * which is the defect the composed summary was built to fix in the first place.
 *
 * ⚠️ A FIELD THE RECORD DOES NOT ANSWER IS OMITTED, NOT BLANKED. Rendering
 * "Score: —" four times makes a thin record look populated.
 */
export function championEvidence(c) {
  const s = c?.story || {}
  const rows = [
    ['Title wording', s.titleLine],
    ['Final score', s.score],
    ['Runner-up', s.runnerUp],
    ['Season record', s.record],
    ['Venue', s.venue],
    ['Head coach', s.coach],
  ]
  return rows
    .filter(([, v]) => String(v || '').trim())
    .map(([k, v]) => ({ k, v: String(v).trim() }))
}
